import {IS_HIGH_DPR, WALL_HEIGHT_COEFFICIENT} from "ui/config";

/**
 * Canvas Clipping Area Helper
 */
class CanvasClipArea {
	/**
	 * Class constructor
	 *
	 * @param {number} sceneWidth
	 * @param {number} sceneHeight
	 */
	constructor(sceneWidth, sceneHeight) {
		/**
		 * @type {number}
		 * @private
		 */
		this._sceneWidth = sceneWidth;
		/**
		 * @type {number}
		 * @private
		 */
		this._sceneHeight = sceneHeight;
		/**
		 * @type {Map}
		 * @private
		 */
		this._cache = new Map();
	}

	/**
	 * Returns scene clipping properties
	 *
	 * @param {number} [padding]
	 * @returns {T_SceneBoundary}
	 */
	getProperties(padding = 0) {
		let key = 'props_p' + padding;
		let props = this._cache.get(key);
		if (props) {
			return props;
		}
		let sceneHeight = this._sceneHeight - (padding << 1),
			sceneWidth = this._sceneWidth - (padding << 1),
			wallHeight = Math.round(sceneHeight * WALL_HEIGHT_COEFFICIENT),
			floorHeight = Math.round(sceneHeight - wallHeight),
			floorHalfHeight = Math.round(floorHeight / 2),
			halfWidth = Math.round(sceneWidth / 2),
			wallTop = sceneHeight - floorHalfHeight - wallHeight,
			wallLength = Math.sqrt(wallTop * wallTop + halfWidth * halfWidth),
			wallAngle = Math.acos(wallTop / wallLength),
			nx = halfWidth / wallLength,
			ny = wallTop / wallLength;

		props = {
			wallHeight,
			floorHeight,
			floorHalfHeight,
			halfWidth,
			wallAngle,
			nx, ny
		};
		this._cache.set(key, props);
		return props;
	}

	/**
	 * Returns path of the clipping area border
	 *
	 * @param {number} [expand]
	 * @returns {Array<T_Point>}
	 */
	getBorderPath(expand = 0) {
		if (this._cache.has('border-path-' + expand)) {
			return this._cache.get('border-path-' + expand);
		}
		let {wallHeight, floorHalfHeight, halfWidth} = this.getProperties();
		let path = [
			{x: halfWidth, y: -expand},
			{x: -expand, y: this._sceneHeight - floorHalfHeight - wallHeight},
			{x: -expand, y: this._sceneHeight - floorHalfHeight},
			{x: halfWidth, y: this._sceneHeight + expand},
			{x: this._sceneWidth + expand, y: this._sceneHeight - floorHalfHeight},
			{x: this._sceneWidth + expand, y: this._sceneHeight - floorHalfHeight - wallHeight},
		];
		this._cache.set('border-path-' + expand, path);
		return path;
	}


	/**
	 * Returns path of the clipping only floor area
	 *
	 * @param {number} [expand]
	 * @returns {Array<T_Point>}
	 */
	getFloorPath(expand = 0) {
		if (this._cache.has('floor-path-' + expand)) {
			return this._cache.get('floor-path-' + expand);
		}
		let {wallHeight, floorHalfHeight, halfWidth} = this.getProperties();
		let path = [
			{x: halfWidth, y: wallHeight - expand},
			{x: this._sceneWidth + expand, y: wallHeight + floorHalfHeight},
			{x: halfWidth, y: this._sceneHeight + expand},
			{x: -expand, y: wallHeight + floorHalfHeight},
		];
		this._cache.set('floor-path-' + expand, path);
		return path;
	}

	/**
	 * Creates clip page on the specified canvas object
	 *
	 * @param {CanvasRenderingContext2D} context
	 * @param {number} [offsetX]
	 * @param {number} [offsetY]
	 * @param {number} [scaleFactor]
	 * @param {number} [expand]
	 * @returns {void}
	 */
	createBorderPath(context, offsetX = 0, offsetY = 0, scaleFactor = 1, expand = 0) {
		context.beginPath();
		let isMoved = false;
		for (let p of this.getBorderPath(expand)) {
			if (!isMoved) {
				context.moveTo(offsetX + (p.x * scaleFactor), offsetY + (p.y * scaleFactor));
				isMoved = true;
			} else {
				context.lineTo(offsetX + (p.x * scaleFactor), offsetY + (p.y * scaleFactor));
			}
		}
	}

	/**
	 * Creates clip page on the specified canvas object
	 *
	 * @param {CanvasRenderingContext2D} context
	 * @param {number} [offsetX]
	 * @param {number} [offsetY]
	 * @param {number} [scaleFactor]
	 * @returns {void}
	 */
	createFloorPath(context, offsetX = 0, offsetY = 0, scaleFactor = 1) {
		context.beginPath();
		let isMoved = false;
		for (let p of this.getFloorPath()) {
			if (!isMoved) {
				context.moveTo(offsetX + (p.x * scaleFactor), offsetY + (p.y * scaleFactor));
				isMoved = true;
			} else {
				context.lineTo(offsetX + (p.x * scaleFactor), offsetY + (p.y * scaleFactor));
			}
		}
	}

	/**
	 * Draws borders
	 *
	 * @param {CanvasRenderingContext2D} context
	 * @param {number} [offsetX]
	 * @param {number} [offsetY]
	 * @param {number} [scaleFactor]
	 * @returns {void}
	 */
	drawBorders(context, offsetX = 0, offsetY = 0, scaleFactor = 1) {
		offsetX *= -1;
		offsetY *= -1;
		context.save();
		context.beginPath();
		context.strokeStyle = '#8dfff6';
		let isMoved = false;
		for (let p of this.getBorderPath()) {
			if (!isMoved) {
				context.moveTo((offsetX + p.x) * scaleFactor, (offsetY + p.y) * scaleFactor);
				isMoved = true;
			} else {
				context.lineTo((offsetX + p.x) * scaleFactor, (offsetY + p.y) * scaleFactor);
			}
		}
		context.closePath();
		context.stroke();
		context.restore();
	}

	/**
	 * Draws scene slipping area grid
	 *
	 * @param {CanvasRenderingContext2D} context
	 * @param {number} [offsetX]
	 * @param {number} [offsetY]
	 * @param {number} [scaleFactor]
	 * @param {number} [padding]
	 */
	drawGrid(context, offsetX = 0, offsetY = 0, scaleFactor = 1, padding = 0) {
		offsetX *= -1;
		offsetY *= -1;
		offsetX += padding;
		offsetY += padding;
		context.save();
		context.beginPath();
		context.lineWidth = IS_HIGH_DPR ? 2 : 1;
		context.strokeStyle = 'rgba(141, 255, 246, 0.3)';

		let {halfWidth, floorHalfHeight, wallHeight, wallAngle} = this.getProperties(padding),
			sceneHeight = this._sceneHeight - (padding << 1),
			wallStepsX = 32,
			wallBigStepsX = 4,
			wallStepsY = 7,
			floorSteps = 32,
			floorBigSteps = 2,
			paddingAngleShift = padding * Math.cos(wallAngle),
			wallHeightScaled = (wallHeight - paddingAngleShift * 2) * scaleFactor,
			halfWidthScaled = halfWidth * scaleFactor,
			floorHalfHeightScaled = floorHalfHeight * scaleFactor,
			wallTop = sceneHeight - floorHalfHeight - wallHeight,
			wallTopScaled = wallTop * scaleFactor,
			wallDy = wallTop / wallStepsX,
			wallBigDy = wallTop / wallBigStepsX,
			wallDx = halfWidth / wallStepsX,
			wallBigDx = halfWidth / wallBigStepsX,
			wallHeightDy = (wallHeight - paddingAngleShift * 2) / wallStepsY,
			floorDx = halfWidth / floorSteps,
			floorBigDx = halfWidth / floorBigSteps,
			floorDy = floorHalfHeight / floorSteps,
			floorBigDy = floorHalfHeight / floorBigSteps,
			ox, oy, x1, x2, y1, y2;

		// draw left-wall vertical lines
		oy = offsetY + (wallTop + paddingAngleShift);
		for (let i = 0; i < wallStepsX; i++) {
			y1 = (oy - (i * wallDy)) * scaleFactor;
			y2 = y1 + wallHeightScaled;
			x1 = (offsetX + i * wallDx) * scaleFactor;
			context.moveTo(x1, y1);
			context.lineTo(x1, y2);
		}

		// draw left-wall horizontal lines
		x1 = offsetX * scaleFactor;
		for (let i = 0; i <= wallStepsY; i++) {
			y1 = (oy + (i * wallHeightDy)) * scaleFactor;
			y2 = y1 - wallTopScaled;
			x2 = x1 + halfWidthScaled;
			context.moveTo(x1, y1);
			context.lineTo(x2, y2);
		}

		// draw right-wall vertical lines
		ox = offsetX + halfWidth;
		for (let i = 0; i <= wallStepsX; i++) {
			y1 = (offsetY + paddingAngleShift + (i * wallDy)) * scaleFactor;
			y2 = y1 + wallHeightScaled;
			x1 = (ox + i * wallDx) * scaleFactor;
			context.moveTo(x1, y1);
			context.lineTo(x1, y2);
		}

		// draw right-wall horizontal lines
		x1 = (offsetX + halfWidth) * scaleFactor;
		x2 = x1 + halfWidthScaled;
		for (let i = 0; i <= wallStepsY; i++) {
			y1 = (offsetY + paddingAngleShift + (i * wallHeightDy)) * scaleFactor;
			y2 = y1 + wallTopScaled;
			context.moveTo(x1, y1);
			context.lineTo(x2, y2);
		}

		// draw floor lines 1
		oy = offsetY + wallHeight + floorHalfHeight - paddingAngleShift;
		for (let i = 0; i <= floorSteps; i++) {
			y1 = (oy + (i * floorDy)) * scaleFactor;
			y2 = y1 - floorHalfHeightScaled;
			x1 = (offsetX + i * floorDx) * scaleFactor;
			x2 = x1 + halfWidthScaled;
			context.moveTo(x1, y1);
			context.lineTo(x2, y2);
		}

		// draw floor lines 2
		for (let i = 0; i <= floorSteps; i++) {
			y1 = (oy - (i * floorDy)) * scaleFactor;
			y2 = y1 + floorHalfHeightScaled;
			x1 = (offsetX + i * floorDx) * scaleFactor;
			x2 = x1 + halfWidthScaled;
			context.moveTo(x1, y1);
			context.lineTo(x2, y2);
		}

		context.stroke();

		context.beginPath();
		context.strokeStyle = 'rgba(141, 255, 246, 0.5)';
		context.lineWidth = 2;

		// draw left-wall vertical lines
		oy = offsetY + (wallTop + paddingAngleShift);
		for (let i = 0; i < wallBigStepsX; i++) {
			y1 = (oy - (i * wallBigDy)) * scaleFactor;
			y2 = y1 + wallHeightScaled;
			x1 = (offsetX + i * wallBigDx) * scaleFactor;
			context.moveTo(x1, y1);
			context.lineTo(x1, y2);
		}

		// draw right-wall vertical lines
		ox = offsetX + halfWidth;
		for (let i = 0; i <= wallBigStepsX; i++) {
			y1 = (offsetY + paddingAngleShift + (i * wallBigDy)) * scaleFactor;
			y2 = y1 + wallHeightScaled;
			x1 = (ox + i * wallBigDx) * scaleFactor;
			context.moveTo(x1, y1);
			context.lineTo(x1, y2);
		}

		// draw floor lines 1
		oy = offsetY + wallHeight + floorHalfHeight - paddingAngleShift;
		for (let i = 0; i <= floorBigSteps; i++) {
			y1 = (oy + (i * floorBigDy)) * scaleFactor;
			y2 = y1 - floorHalfHeightScaled;
			x1 = (offsetX + i * floorBigDx) * scaleFactor;
			x2 = x1 + halfWidthScaled;
			context.moveTo(x1, y1);
			context.lineTo(x2, y2);
		}

		// draw floor lines 2
		for (let i = 0; i <= floorBigSteps; i++) {
			y1 = (oy - (i * floorBigDy)) * scaleFactor;
			y2 = y1 + floorHalfHeightScaled;
			x1 = (offsetX + i * floorBigDx) * scaleFactor;
			x2 = x1 + halfWidthScaled;
			context.moveTo(x1, y1);
			context.lineTo(x2, y2);
		}

		context.stroke();

		context.restore();
	}
}

export default CanvasClipArea;
